import { API_CONFIG } from '../config/api';
import { getTokenIfValid } from './session';

// Este módulo usa fetch crudo (no el axios de services/api.ts), así que no hay
// interceptor que agregue el token: hay que ponerlo a mano. El parámetro `token`
// de las funciones de abajo es opcional y en la práctica ningún llamador lo pasa,
// por lo que se resuelve desde la sesión activa. Sin esto, las rutas que exigen
// PROGRAMACION_ACCESS en el backend responden 401.
const resolveToken = (token?: string): string | undefined =>
  token ?? getTokenIfValid() ?? undefined;

const authHeaders = (token?: string, json = false): Record<string, string> => {
  const bearer = resolveToken(token);
  return {
    Accept: 'application/json',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
  };
};

export type PlanificacionItem = {
  rut: string;
  fecha_trabajo: string; // YYYY-MM-DD
  cliente_id?: number | string;
  nodo_id?: number | string;
  // Nodo de servicio estándar (TAREA 7): null/omitido => fila principal del cliente.
  nodo_servicio_id?: number | string | null;
  horas_estimadas?: number;
  observaciones?: string;
  estado?: 'activo' | 'eliminado';
};

export interface SaveWeekBody {
  cartera_id: number | string;
  semana_inicio: string; // YYYY-MM-DD (lunes)
  usuario_id?: number | string;
  planificaciones: PlanificacionItem[];
}

export interface SaveWeekResponse {
  success: boolean;
  created?: number;
  updated?: number;
  errors?: Array<{ message?: string; field?: string }>; 
  message?: string;
}

export const saveWeek = async (body: SaveWeekBody, token?: string): Promise<SaveWeekResponse> => {
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/save-week`;
  console.debug('[programacionSemanal.saveWeek] POST', url, { body });
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(body),
  });
  console.debug('[programacionSemanal.saveWeek] status', res.status);
  const data = (await res.json()) as SaveWeekResponse;
  console.debug('[programacionSemanal.saveWeek] response', data);
  if (!res.ok || data?.success === false) {
    const msg = data?.message || 'Error guardando planificación semanal';
    throw new Error(msg);
  }
  return data;
};

// Copiar semana origen -> destino (origen + 7 días en backend)
export interface CopiarSemanaBody {
  cartera_id: number | string;
  fecha_inicio: string; // lunes de la semana origen YYYY-MM-DD
  overwrite?: boolean;
  cliente_id?: number | string; // opcional para copiar solo un cliente
}

export interface CopiarSemanaResponse {
  success: boolean;
  message?: string;
  data?: { asignaciones_copiadas?: number; errores?: number };
}

export const copiarSemana = async (body: CopiarSemanaBody, token?: string): Promise<CopiarSemanaResponse> => {
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/copiar-semana`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as CopiarSemanaResponse;
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || 'Error al copiar semana');
  }
  return data;
};

// Obtener semanas históricas para una cartera (lista de lunes)
export interface SemanaHistorialItem {
  semana_inicio: string; // YYYY-MM-DD (lunes)
  semana_fin?: string;   // YYYY-MM-DD (domingo)
  total_asignaciones?: number;
  cambios_count?: number;
}

export interface GetSemanasHistorialResponse {
  success: boolean;
  data?: SemanaHistorialItem[];
}

export const getSemanasHistorial = async (
  cartera_id?: string | number,
  cliente_id?: string | number,
  token?: string
): Promise<SemanaHistorialItem[]> => {
  const params = new URLSearchParams();
  if (cartera_id !== undefined && cartera_id !== null && `${cartera_id}` !== '') {
    params.set('cartera_id', String(cartera_id));
  }
  if (cliente_id !== undefined && cliente_id !== null && `${cliente_id}` !== '') {
    params.set('cliente_id', String(cliente_id));
  }
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/history${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const data = (await res.json()) as GetSemanasHistorialResponse;
  if (!res.ok || data?.success === false) {
    throw new Error('No se pudo obtener el historial de semanas');
  }
  return data.data || [];
};

// Responsable de cartera (IA = Ingeniero de Aplicación, IS = Ingeniero de Servicios)
export interface CarteraResponsable {
  rut: string;
  nombre?: string | null;
}

// Vacaciones / licencia médica que se solapan con la semana (rango, global por persona)
export interface EstadoRango {
  rut: string;
  nombre?: string | null;
  estado: string;        // 'vacaciones' | 'licencia medica' | ...
  desde: string;         // ISO
  hasta: string | null;  // ISO o null (abierto)
  comentario?: string | null;
}

// Marca por día: compensado (día de devolución) / parqtec (comodín oficina) /
// el_rosal (personal en la planta El Rosal ese día).
export type MarcaTipo = 'compensado' | 'parqtec' | 'el_rosal';
export interface PlanificacionMarca {
  rut: string;
  nombre?: string | null;
  fecha: string;         // ISO
  tipo: MarcaTipo;
  notas?: string | null;
  cartera_id?: number | string;
}

// Obtener programación por rango de fechas
export interface GetProgramacionResponse {
  success: boolean;
  data?: {
    cartera?: {
      id: number | string;
      nombre?: string;
      nombre_alias?: string | null;
      zona?: string | null;
      ia?: CarteraResponsable | null;
      is?: CarteraResponsable | null;
    };
    periodo?: { inicio: string; fin: string };
    programacion?: Array<{
      fecha: string; // YYYY-MM-DD
      dia_semana: string; // 'lunes' .. 'domingo'
      trabajadores: Array<{
        id?: number | string;
        rut: string;
        nombre_persona: string;
        cargo?: string;
        cartera_id: number | string;
        nombre_cartera?: string;
        cliente_id?: number | string;
        nombre_cliente?: string;
        nodo_id?: number | string;
        nombre_nodo?: string;
        // Nodo de servicio estándar (TAREA 7). null/ausente => fila principal del cliente.
        // Mientras el backend no implemente la TAREA 7, estos campos no vienen y el
        // export se comporta exactamente igual que hoy (NODO = cliente).
        nodo_servicio_id?: number | string | null;
        nodo_servicio_nombre?: string | null;
        fecha_trabajo: string;
        horas_estimadas?: number;
        estado?: string;
      }>;
    }>;
    estados?: EstadoRango[];   // vacaciones / licencia médica que solapan la semana
    marcas?: PlanificacionMarca[]; // compensado / parqtec de esta cartera en la semana
  };
}

/**
 * Carga programación de MÚLTIPLES carteras en una sola petición HTTP.
 * El backend resuelve todo en 4 queries paralelas en vez de 4×N.
 *
 * Returns a map { [cartera_id]: GetProgramacionResponse['data'] }
 */
export const getProgramacionRangoBatch = async (
  cartera_ids: Array<string | number>,
  fecha_inicio: string,
  fecha_fin: string,
  token?: string
): Promise<Record<string, GetProgramacionResponse['data']>> => {
  if (cartera_ids.length === 0) return {};
  const params = new URLSearchParams({
    cartera_ids: cartera_ids.join(','),
    fecha_inicio,
    fecha_fin,
  });
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/batch?${params.toString()}`;
  console.debug('[programacionSemanal.getProgramacionRangoBatch] GET', url, { carteras: cartera_ids.length });
  const res = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const data = await res.json();
  if (!res.ok || data?.success === false) {
    throw new Error('No se pudo obtener la programación (batch)');
  }
  return data.data as Record<string, GetProgramacionResponse['data']>;
};

export const getProgramacionRango = async (
  cartera_id: string | number,
  fecha_inicio: string,
  fecha_fin?: string,
  cliente_id?: string | number,
  detail?: boolean,
  token?: string
) => {
  const params = new URLSearchParams();
  params.set('cartera_id', String(cartera_id));
  params.set('fecha_inicio', fecha_inicio);
  if (fecha_fin) params.set('fecha_fin', fecha_fin);
  if (cliente_id !== undefined && cliente_id !== null && `${cliente_id}` !== '') params.set('cliente_id', String(cliente_id));
  if (detail) params.set('detail', 'true');
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal?${params.toString()}`;
  console.debug('[programacionSemanal.getProgramacionRango] GET', url);
  const res = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const data = (await res.json()) as GetProgramacionResponse;
  const count = Array.isArray(data?.data?.programacion) ? data.data!.programacion!.reduce((n, d: any) => n + ((d?.trabajadores || []).length), 0) : 0;
  console.debug('[programacionSemanal.getProgramacionRango] status', res.status, 'items', count);
  if (!res.ok || data?.success === false) {
    throw new Error('No se pudo obtener la programación del rango solicitado');
  }
  return data.data;
};

// Detalle de historial para un rango (usado por semana)
export interface HistEntry {
  id: number;
  programacion_id: number;
  rut: string;
  cartera_id: string | number;
  accion: 'creado' | 'actualizado' | 'eliminado' | string;
  cambios: any; // { before?, after? }
  fecha_accion: string; // ISO
  usuario?: string;
}

export interface GetHistorialSemanalResponse {
  success: boolean;
  data?: HistEntry[];
}

export const getHistorialSemanalDetalle = async (
  cartera_id: string | number | undefined,
  semana_inicio: string, // YYYY-MM-DD
  semana_fin?: string,   // YYYY-MM-DD
  cliente_id?: string | number,
  limit?: number,
  detalle?: boolean,
  token?: string
): Promise<HistEntry[]> => {
  const params = new URLSearchParams({
    fecha_inicio: semana_inicio,
  });
  if (cartera_id !== undefined && cartera_id !== null && `${cartera_id}` !== '') params.set('cartera_id', String(cartera_id));
  if (semana_fin) params.set('fecha_fin', semana_fin);
  if (cliente_id !== undefined && cliente_id !== null && `${cliente_id}` !== '') params.set('cliente_id', String(cliente_id));
  if (limit) params.set('limit', String(limit));
  if (detalle) params.set('detalle', 'true');
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/history?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const data = (await res.json()) as GetHistorialSemanalResponse | { data: any };
  if (!res.ok || (data as any)?.success === false) {
    throw new Error('No se pudo obtener el detalle de historial');
  }
  // Algunos backends devuelven { success, data: { semanas:[], cambios:[] } }; contemplar data.data si es array
  const d: any = (data as any).data;
  if (Array.isArray(d)) return d as HistEntry[];
  if (d?.cambios && Array.isArray(d.cambios)) return d.cambios as HistEntry[];
  return [];
};

// Historial por item específico
export const getItemHistorial = async (
  programacion_id: number | string,
  token?: string
): Promise<HistEntry[]> => {
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/${programacion_id}/history`;
  const res = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || 'No se pudo obtener el historial del item');
  }
  const arr = (data?.data || []) as any[];
  // Normalizar campos mínimos
  return Array.isArray(arr) ? arr.map((e: any) => ({
    id: e.id ?? 0,
    programacion_id: Number(programacion_id) as any,
    rut: e.rut ?? '',
    cartera_id: e.cartera_id ?? '',
    accion: e.accion,
    cambios: e.cambios,
    fecha_accion: e.fecha_accion,
    usuario: e.usuario,
  })) : [];
};

// Eliminar historial agregado de una semana (opcionalmente por cartera)
export const deleteHistorialSemana = async (
  semana_inicio: string,
  cartera_id?: string | number,
  cliente_id?: string | number,
  token?: string
): Promise<number> => {
  // Normalizar a YYYY-MM-DD
  const toYMD = (v: string) => {
    if (!v) return v;
    if (v.includes('T')) {
      const d = new Date(v);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    }
    return v;
  };
  const semana = toYMD(semana_inicio);
  const params = new URLSearchParams({ fecha_inicio: semana });
  if (cartera_id !== undefined && cartera_id !== null && `${cartera_id}` !== '') params.set('cartera_id', String(cartera_id));
  if (cliente_id !== undefined && cliente_id !== null && `${cliente_id}` !== '') params.set('cliente_id', String(cliente_id));
  const url = `${API_CONFIG.BASE_URL}/programacion-semanal/history?${params.toString()}`;
  // Usar POST endpoint explícito creado en backend: /programacion-semanal/history/delete
  console.debug('[programacionSemanal.deleteHistorialSemana] body', { fecha_inicio: semana, cartera_id, cliente_id });
  try {
    const resp = await fetch(`${API_CONFIG.BASE_URL}/programacion-semanal/history/delete`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ fecha_inicio: semana, ...(cartera_id ? { cartera_id } : {}), ...(cliente_id ? { cliente_id } : {}) }),
    });
    console.debug('[programacionSemanal.deleteHistorialSemana] status', resp.status);
    if (resp.status === 404) return 0; // no había programación en la semana
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.message || `Error eliminando historial (${resp.status})`);
    }
    const body = await resp.json().catch(() => ({}));
    console.debug('[programacionSemanal.deleteHistorialSemana] response', body);
    // devolver cantidad eliminada si el backend la retorna
    return typeof body?.deleted === 'number' ? body.deleted : 0;
  } catch (err) {
    console.warn('[programacionSemanal.deleteHistorialSemana] POST failed, trying fallback DELETE', err);
    // Como fallback mantener intento por query-delete (por compatibilidad antigua)
    const fallbackRes = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders(token),
    }).catch(() => null);
    if (!fallbackRes) throw err;
    console.debug('[programacionSemanal.deleteHistorialSemana] fallback status', fallbackRes.status);
    if (fallbackRes.status === 404) return 0;
    if (!fallbackRes.ok) {
      try {
        const data = await fallbackRes.json();
        throw new Error(data?.message || 'No se pudo eliminar el historial de la semana');
      } catch {
        throw err;
      }
    }
    const fbBody = await fallbackRes.json().catch(() => ({}));
    console.debug('[programacionSemanal.deleteHistorialSemana] fallback response', fbBody);
    return typeof fbBody?.deleted === 'number' ? fbBody.deleted : 0;
  }
};

// DELETE by-date: eliminar una asignación por rut + fecha
export interface DeleteByDateParams {
  rut: string;
  fecha_trabajo: string; // YYYY-MM-DD
  cartera_id?: string | number;
  cliente_id?: string | number; // no siempre usado por backend, pero permitido
  userHeader?: string; // valor para X-User
  token?: string;
  purgeHistory?: boolean; // si true, también eliminar historial asociado a la semana
}

export interface DeleteByDateResult {
  success: boolean;
  deleted?: number;
  status: number; // HTTP status
  rawBody?: any;
  headers?: Record<string, string>;
}

export const deleteByDate = async (paramsIn: DeleteByDateParams): Promise<DeleteByDateResult> => {
  const params = new URLSearchParams();
  params.set('rut', String(paramsIn.rut));
  params.set('fecha_trabajo', String(paramsIn.fecha_trabajo));
  if (paramsIn.cartera_id !== undefined && paramsIn.cartera_id !== null && `${paramsIn.cartera_id}` !== '') {
    params.set('cartera_id', String(paramsIn.cartera_id));
  }
  if (paramsIn.cliente_id !== undefined && paramsIn.cliente_id !== null && `${paramsIn.cliente_id}` !== '') {
    params.set('cliente_id', String(paramsIn.cliente_id));
  }
  let url = `${API_CONFIG.BASE_URL}/programacion-semanal/by-date?${params.toString()}`;
  let sameOrigin = false;
  try {
    if (typeof window !== 'undefined') {
      const api = new URL(API_CONFIG.BASE_URL);
      if (api.origin !== window.location.origin) {
        // Usar ruta relativa para aprovechar el proxy del dev server y evitar CORS
        url = `/api/programacion-semanal/by-date?${params.toString()}`;
        sameOrigin = true; // relative to current origin
      } else {
        sameOrigin = true;
      }
    }
  } catch {}

  // Evitar preflight rechazado por CORS cuando el backend no permite 'X-User'
  let sendXUser = sameOrigin;

  // Intentar primero con la URL configurada (normalmente local / proxy de dev).
  // Si falla por error de red (backend no escuchando), reintentar con FALLBACK_BASE_URL.
  let res: Response | null = null;
  const makeHeaders = (allowXUser: boolean) => ({
    ...authHeaders(paramsIn.token),
    ...(paramsIn.userHeader && allowXUser ? { 'X-User': paramsIn.userHeader } : {}),
  });

  try {
    res = await fetch(url, {
      method: 'DELETE',
      headers: makeHeaders(sendXUser),
    });
  } catch (err) {
    console.warn('[programacionSemanal.deleteByDate] primary DELETE failed, will try fallback', err);
    // Construir URL absoluta para fallback (usar FALLBACK_BASE_URL si existe,
    // o usar BASE_URL como último recurso) y normalizar slash final.
    try {
      const fallbackBase: string = ((API_CONFIG as any).FALLBACK_BASE_URL) || API_CONFIG.BASE_URL;
      const baseNoSlash = String(fallbackBase).replace(/\/$/, '');
      const fallbackUrl = `${baseNoSlash}/programacion-semanal/by-date?${params.toString()}`;
      // No enviar X-User al fallback para evitar preflight si es cross-origin
      res = await fetch(fallbackUrl, {
        method: 'DELETE',
        headers: makeHeaders(false),
      });
    } catch (err2) {
      console.error('[programacionSemanal.deleteByDate] fallback DELETE also failed', err2);
      throw err2;
    }
  }

  // Leer headers
  let headers: Record<string, string> = {};
  try {
    res.headers.forEach((v, k) => { headers[k] = v; });
  } catch {}

  // Idempotencia: 204 (sin contenido) o 404 (no existía) se tratan como éxito
  if (res.status === 204 || res.status === 404) {
    return { success: true, deleted: 0, status: res.status, headers };
  }

  // Leer cuerpo como texto y tratar de parsear JSON (mantener rawBody para diagnóstico)
  let txt = await res.text().catch(() => '');
  let body: any = null;
  try {
    body = txt ? JSON.parse(txt) : null;
  } catch (err) {
    body = txt || null;
  }

  // Detectar si la respuesta es HTML (CDN devolviendo SPA index.html). Si ocurre,
  // reintentar UNA vez contra el `FALLBACK_BASE_URL` (o `BASE_URL` si no existe). Esto
  // ayuda cuando un CDN responde con la SPA en vez del backend real.
  const isHtmlResponse = (() => {
    try {
      const ct = (headers['content-type'] || headers['Content-Type'] || '').toString().toLowerCase();
      if (ct.includes('text/html')) return true;
    } catch {}
    if (typeof txt === 'string') {
      const t = txt.trim().toLowerCase();
      if (t.startsWith('<!doctype') || t.startsWith('<html')) return true;
    }
    return false;
  })();

  let attemptedHtmlFallback = false;
  if (isHtmlResponse) {
    attemptedHtmlFallback = true;
    try {
      console.warn('[programacionSemanal.deleteByDate] HTML response detected (probable CDN). Retrying with fallback base URL.');
      const fallbackBase: string = ((API_CONFIG as any).FALLBACK_BASE_URL) || API_CONFIG.BASE_URL;
      const baseNoSlash = String(fallbackBase).replace(/\/$/, '');
      const fallbackUrl = `${baseNoSlash}/programacion-semanal/by-date?${params.toString()}`;
      const res2 = await fetch(fallbackUrl, {
        method: 'DELETE',
        headers: makeHeaders(false),
      });
      // Releer headers y cuerpo desde fallback
      const headers2: Record<string, string> = {};
      try { res2.headers.forEach((v, k) => { headers2[k] = v; }); } catch {}
      const txt2 = await res2.text().catch(() => '');
      let body2: any = null;
      try { body2 = txt2 ? JSON.parse(txt2) : null; } catch { body2 = txt2 || null; }
      // Reasignar para continuar el flujo con la respuesta de fallback
      res = res2;
      headers = headers2;
      txt = txt2;
      body = body2;
    } catch (err) {
      console.error('[programacionSemanal.deleteByDate] Fallback after HTML response failed', err);
      // continuar con la respuesta original (que era HTML)
    }
  }

  // Aceptar respuestas fallback del backend donde la fila se marca como eliminada en lugar
  // de retornar un conteo físico. Ejemplos esperados: { marked_deleted: true } o
  // { estado: 'eliminado' } o { deleted: 0, fallback_marked: true }
  const fallbackMarked = Boolean(
    body && (
      body.marked_deleted === true ||
      body.fallback_marked === true ||
      body.estado === 'eliminado' ||
      (typeof body.deleted === 'number' && body.deleted > 0)
    )
  );

  const ok = res.ok || fallbackMarked;
  if (!ok) {
    let msg = `Error eliminando asignación (${res.status})`;
    if (body && typeof body === 'object' && body.message) msg = body.message;
    else if (typeof body === 'string' && body.length) msg = body;
    throw new Error(msg);
  }

  console.debug('[programacionSemanal.deleteByDate] final response', { status: res.status, deleted: typeof body?.deleted === 'number' ? body.deleted : (fallbackMarked ? 0 : undefined), rawBody: body, headers, attemptedHtmlFallback });
  return {
    success: true,
    deleted: typeof body?.deleted === 'number' ? body.deleted : (fallbackMarked ? 0 : undefined),
    status: res.status,
    rawBody: body,
    headers,
  };
};

// ============================================================================
// Estados con rango (vacaciones / licencia médica) — mantenimiento.personal_estados
// ============================================================================

// Asignar a una persona un estado por rango (p. ej. vacaciones del 9 al 13).
// `estado` acepta el nombre ('vacaciones', 'licencia medica') o se puede pasar estado_id.
export const setEstadoRango = async (
  body: { rut: string; estado?: string; estado_id?: number; desde: string; hasta?: string | null; comentario?: string },
  token?: string
): Promise<EstadoRango & { id: number }> => {
  const url = `${API_CONFIG.BASE_URL}/personal-estados`;
  const res = await fetch(url, { method: 'POST', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || 'No se pudo registrar el estado');
  }
  return data.data;
};

// Eliminar un registro de estado por id
export const deleteEstadoRango = async (id: number | string, token?: string): Promise<void> => {
  const url = `${API_CONFIG.BASE_URL}/personal-estados/${id}`;
  const res = await fetch(url, { method: 'DELETE', headers: authHeaders(token) });
  if (!res.ok && res.status !== 404) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || 'No se pudo eliminar el estado');
  }
};

// Personal cuyo estado se solapa con un rango (vacaciones/licencia de la semana)
export const getEstadosOverlap = async (
  fecha_inicio: string,
  fecha_fin: string,
  estados?: string, // 'vacaciones,licencia medica'
  token?: string
): Promise<EstadoRango[]> => {
  const params = new URLSearchParams({ fecha_inicio, fecha_fin });
  if (estados) params.set('estados', estados);
  const url = `${API_CONFIG.BASE_URL}/personal-estados/overlap?${params.toString()}`;
  const res = await fetch(url, { method: 'GET', headers: authHeaders(token) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error('No se pudo obtener estados por rango');
  return (data.data || []) as EstadoRango[];
};

// Evento de estado tal como lo devuelve el backend, con su id de fila.
// Nota: GET /personal-estados/persona/:rut devuelve el nombre del estado en `estado_nombre`,
// no en `estado` (a diferencia de /programacion-semanal, que sí usa `estado`). Se declara
// `estado` opcional y se agrega `estado_nombre` para reflejar la forma real de la respuesta;
// los consumidores deben revisar ambos campos.
export type EstadoRangoEvento = Omit<EstadoRango, 'estado'> & { id: number; estado_id?: number; estado?: string; estado_nombre?: string };

// Todos los eventos de estado (vacaciones/licencia) de una persona.
// Ajuste exacto para "dame el rango de esta persona" (no usar /overlap, que es por semana y para todos).
export const getEstadosPersona = async (
  rut: string,
  token?: string
): Promise<EstadoRangoEvento[]> => {
  const url = `${API_CONFIG.BASE_URL}/personal-estados/persona/${encodeURIComponent(rut)}`;
  const res = await fetch(url, { method: 'GET', headers: authHeaders(token) });
  if (res.status === 404) return []; // sin eventos
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudieron obtener los estados de la persona');
  return (data.data || []) as EstadoRangoEvento[];
};

// Editar un evento de estado existente (rango). Evita crear filas duplicadas.
export const updateEstadoRango = async (
  id: number | string,
  body: { estado?: string; estado_id?: number; desde?: string; hasta?: string | null; comentario?: string },
  token?: string
): Promise<EstadoRangoEvento> => {
  const url = `${API_CONFIG.BASE_URL}/personal-estados/${id}`;
  const res = await fetch(url, { method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo actualizar el estado');
  return data.data as EstadoRangoEvento;
};

// ============================================================================
// Marcas por día (compensado / parqtec) — zc_temp.planificacion_marca
// ============================================================================

export const getMarcas = async (
  cartera_id: number | string,
  fecha_inicio: string,
  fecha_fin: string,
  token?: string
): Promise<PlanificacionMarca[]> => {
  const params = new URLSearchParams({ fecha_inicio, fecha_fin, cartera_id: String(cartera_id) });
  const url = `${API_CONFIG.BASE_URL}/planificacion-marca?${params.toString()}`;
  const res = await fetch(url, { method: 'GET', headers: authHeaders(token) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error('No se pudieron obtener las marcas');
  return (data.data || []) as PlanificacionMarca[];
};

export const setMarca = async (
  body: { rut: string; fecha: string; cartera_id: number | string; tipo: MarcaTipo; notas?: string },
  token?: string
): Promise<PlanificacionMarca> => {
  const url = `${API_CONFIG.BASE_URL}/planificacion-marca`;
  const res = await fetch(url, { method: 'POST', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo registrar la marca');
  return data.data as PlanificacionMarca;
};

export const deleteMarca = async (
  rut: string,
  fecha: string,
  cartera_id: number | string,
  token?: string
): Promise<void> => {
  const params = new URLSearchParams({ rut, fecha, cartera_id: String(cartera_id) });
  const url = `${API_CONFIG.BASE_URL}/planificacion-marca?${params.toString()}`;
  const res = await fetch(url, { method: 'DELETE', headers: authHeaders(token) });
  if (!res.ok && res.status !== 204 && res.status !== 404) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || 'No se pudo eliminar la marca');
  }
};

// ============================================================================
// Capacitaciones programadas (por día) — zc_temp.capacitaciones_programadas
// Módulo de planificación (TAREA 5). Mismo estilo que planificacion_marca.
// ============================================================================

export interface Capacitacion {
  id: number;
  rut: string;
  nombre_persona?: string;
  fecha: string;          // YYYY-MM-DD
  capacitacion?: string;  // nombre de la capacitación (opcional)
  cartera_id?: number | string | null;
}

export const getCapacitacionesRango = async (
  fecha_inicio: string,
  fecha_fin: string,
  cartera_id?: number | string,
  token?: string,
): Promise<Capacitacion[]> => {
  const params = new URLSearchParams({ fecha_inicio, fecha_fin });
  if (cartera_id !== undefined && cartera_id !== null && `${cartera_id}` !== '') params.set('cartera_id', String(cartera_id));
  const url = `${API_CONFIG.BASE_URL}/capacitaciones?${params.toString()}`;
  const res = await fetch(url, { method: 'GET', headers: authHeaders(token) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudieron obtener las capacitaciones');
  return (data.data || []) as Capacitacion[];
};

export const setCapacitacion = async (
  body: { rut: string; fecha: string; nombre?: string; cartera_id?: number | string },
  token?: string,
): Promise<Capacitacion> => {
  const url = `${API_CONFIG.BASE_URL}/capacitaciones`;
  const res = await fetch(url, { method: 'POST', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo registrar la capacitación');
  return data.data as Capacitacion;
};

export const deleteCapacitacion = async (id: number | string, token?: string): Promise<void> => {
  const url = `${API_CONFIG.BASE_URL}/capacitaciones/${id}`;
  const res = await fetch(url, { method: 'DELETE', headers: authHeaders(token) });
  if (!res.ok && res.status !== 204 && res.status !== 404) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || 'No se pudo eliminar la capacitación');
  }
};

// ============================================================================
// Metadatos de cartera (IA/IS + zona) — zc_temp.cartera_meta
// ============================================================================

export const setCarteraMeta = async (
  cartera_id: number | string,
  // `nombre` = alias visible de la cartera (no afecta a clientes). Requiere columna
  // `nombre_alias` en zc_temp.cartera_meta (ver docs backend).
  // `disponible` = si la cartera se muestra en Planificación (columna
  // `disponible_planificacion` en zc_temp.cartera_meta, ver docs backend).
  body: { ia_rut?: string; is_rut?: string; zona?: string; nombre?: string | null; disponible?: boolean },
  token?: string
): Promise<{ cartera_id: string; ia_rut: string | null; is_rut: string | null; zona: string | null; nombre?: string | null; disponible?: boolean }> => {
  const url = `${API_CONFIG.BASE_URL}/carteras/${cartera_id}/meta`;
  const res = await fetch(url, { method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo actualizar la cartera');
  return data.data;
};

// ============================================================================
// Metadatos de cliente/nodo (IA/IS) — zc_temp.cliente_meta / zc_temp.nodo_meta
// Mismo patrón que cartera_meta, pero sin zona/alias/disponible.
// ============================================================================

export interface ClienteNodoMetaRow {
  ia: { rut: string; nombre?: string | null } | null;
  is: { rut: string; nombre?: string | null } | null;
}

export const getClientesMeta = async (
  token?: string
): Promise<Array<{ cliente_id: string } & ClienteNodoMetaRow>> => {
  const url = `${API_CONFIG.BASE_URL}/servicios/clientes-meta`;
  const res = await fetch(url, { headers: authHeaders(token) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo obtener la meta de clientes');
  return data.data || [];
};

export const setClienteMeta = async (
  cliente_id: number | string,
  body: { ia_rut?: string; is_rut?: string },
  token?: string
): Promise<{ cliente_id: string; ia_rut: string | null; is_rut: string | null }> => {
  const url = `${API_CONFIG.BASE_URL}/servicios/clientes/${cliente_id}/meta`;
  const res = await fetch(url, { method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo actualizar el cliente');
  return data.data;
};

export const getNodosMeta = async (
  token?: string
): Promise<Array<{ nodo_id: string } & ClienteNodoMetaRow>> => {
  const url = `${API_CONFIG.BASE_URL}/servicios/nodos-meta`;
  const res = await fetch(url, { headers: authHeaders(token) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo obtener la meta de nodos');
  return data.data || [];
};

export const setNodoMeta = async (
  nodo_id: number | string,
  body: { ia_rut?: string; is_rut?: string },
  token?: string
): Promise<{ nodo_id: string; ia_rut: string | null; is_rut: string | null }> => {
  const url = `${API_CONFIG.BASE_URL}/servicios/nodos/${nodo_id}/meta`;
  const res = await fetch(url, { method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) throw new Error(data?.message || 'No se pudo actualizar el nodo');
  return data.data;
};

// ============================================================================
// Catálogo de nodos de servicio estándar (TAREA 7) — mantenimiento.nodos_servicio
// Set global e igual para todos los clientes (APR Asessor / Tec. Confiabilidad /
// Proyectos / Servicio Interno). Best-effort: si el backend aún no expone el
// endpoint, devuelve [] y el export sigue funcionando sin sub-filas.
// ============================================================================

export interface NodoServicio {
  id: number | string;
  codigo: string;
  nombre: string;
  orden: number;
}

// Set estándar por defecto (TAREA 7), igual para todos los clientes. Se usa como
// fallback del frontend mientras el backend no exponga `GET /nodos-servicio`.
// El `id` es el código (string) para que las sub-filas existan aunque el backend
// todavía no tenga la tabla/catálogo. Cuando el backend lo exponga con ids reales,
// esos tienen prioridad (ver getNodosServicio).
export const DEFAULT_NODOS_SERVICIO: NodoServicio[] = [
  { id: 'apr_asessor',          codigo: 'apr_asessor',          nombre: 'APR Asessor',            orden: 1 },
  { id: 'tec_confiabilidad',    codigo: 'tec_confiabilidad',    nombre: 'Tec. Confiabilidad',     orden: 2 },
  { id: 'proyectos',            codigo: 'proyectos',            nombre: 'Proyectos',              orden: 3 },
  { id: 'servicio_interno',     codigo: 'servicio_interno',     nombre: 'Servicio Interno',       orden: 4 },
  { id: 'ingeniero_servicios',  codigo: 'ingeniero_servicios',  nombre: 'Ingeniero en Servicios', orden: 5 },
];

export const getNodosServicio = async (token?: string): Promise<NodoServicio[]> => {
  const url = `${API_CONFIG.BASE_URL}/nodos-servicio`;
  try {
    const res = await fetch(url, { method: 'GET', headers: authHeaders(token) });
    if (!res.ok) return DEFAULT_NODOS_SERVICIO;
    const data = await res.json().catch(() => ({} as any));
    const arr = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
    const mapped = (arr as NodoServicio[])
      .map(n => ({ id: n.id, codigo: n.codigo, nombre: n.nombre, orden: Number(n.orden) || 0 }))
      .sort((a, b) => a.orden - b.orden);
    // Si el backend aún no tiene catálogo, usar el set estándar para que las
    // sub-filas aparezcan igual en la planificación.
    return mapped.length > 0 ? mapped : DEFAULT_NODOS_SERVICIO;
  } catch {
    return DEFAULT_NODOS_SERVICIO;
  }
};

// Helper: obtener lunes YYYY-MM-DD de la semana que contiene la fecha dada
const getMondayYMD = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00Z');
  const day = d.getUTCDay(); // 0..6 (0=domingo)
  const diff = (day === 0) ? -6 : (1 - day); // mover a lunes
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + diff);
  const y = monday.getUTCFullYear();
  const m = String(monday.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(monday.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// Extender: wrapper que elimina by-date y opcionalmente purga historial de la semana
export const deleteByDateAndMaybeHistory = async (paramsIn: DeleteByDateParams): Promise<DeleteByDateResult> => {
  const res = await deleteByDate(paramsIn);
  if (res && res.success && paramsIn.purgeHistory) {
    try {
      const semana_inicio = getMondayYMD(paramsIn.fecha_trabajo);
      // Llamar a deleteHistorialSemana para eliminar historial de la semana
      await deleteHistorialSemana(semana_inicio, paramsIn.cartera_id, paramsIn.cliente_id, paramsIn.token);
    } catch (err) {
      // Registrar pero no bloquear la eliminación primaria
      console.warn('[programacionSemanal] purgeHistory failed', err);
    }
  }
  return res;
};
